import React, { useRef } from 'react';
import { Refine } from '@refinedev/core';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { match } from 'path-to-regexp';
import { Login } from '@pages/Auth/LoginForm';
import { Dashboard } from '@pages/Dashboard/Dashboard';
import { Registration } from '@pages/Auth/Registration';
import { Species } from '@pages/SpeciesSettings/Species';
import { LogoUpload } from '@pages/Profile/ProfileLogoUpload';
import { AddressComponent } from '@pages/Profile/ProfileAddress';
import { TaxDetails } from '@pages/Profile/TaxDetails';
import { Color } from '@pages/SpeciesSettings/Color';
import { Addons } from '@pages/ProductsSettings/Addons';
import { ForgotPassword } from '@pages/ForgotPassword/ForgotPassword';
import { ResetPassword } from '@pages/ForgotPassword/ResetPassword';
import { AuthenticatedRouteProps, AppNotification } from '@helpers/Models';
import { Services } from '@pages/SpeciesSettings/Service';
import { Products } from '@pages/ProductsSettings/Products';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { SocketConfig } from '@helpers/Constants/Navigation';
import { PackagingType } from '@pages/ProductsSettings/PackagingType';
import { RoleManagement } from '@pages/UserManagement/RoleManagement';
import { SpeciesAndService } from '@pages/SpeciesSettings/SpeciesAndService';
import { UserManagement } from '@pages/UserManagement/UserManagement';
import { SpeciesSelection } from '@pages/OrderCreation/SpeciesSelection';
import { OperationManagement } from '@pages/OperationManagement/OperationManagement';
import { CustomerList } from '@pages/OrderCreation/CustomerList';
import { OrderService } from '@pages/OrderCreation/OrderService';
import { OrderCheckin } from '@pages/OrderCreation/OrderCheckin';
import { SpeciesCustomisation } from '@pages/OrderCreation/SpeciesCustomisation';
import CustomerDetails from '@pages/NewOrderCreation/CustomerDetails';
import { OrderSummary } from '@pages/OrderSummary/OrderSummary';
import { OrderPackaging } from '@pages/OrderPackaging/OrderPackaging';
import { EditCustomer } from '@pages/OrderCreation/EditCustomer';
import { ProductDetails } from '@pages/OrderCreation/ProductDetails';
import { SpeciesSummary } from '@pages/OrderSummary/SpeciesSummary';
import { ViewOrderSummary } from '@pages/OrderSummary/ViewOrderSummary';
import { AllOrderList } from '@pages/OrderProcessing/AllOrderList';
import { OrderDetails } from '@pages/OrderSummary/OrderDetails';
import { ServiceProcessingList } from '@pages/OrderProcessing/ServiceProcessingList';
import { ServiceProcessTaskList } from '@pages/OrderProcessing/ServiceProcessTaskList';
import { ServiceProcessTaskDetails } from '@pages/OrderProcessing/ServiceProcessTaskDetails';
import { ProductProcessingList } from '@pages/OrderProcessing/ProductProcessingList';
import { OrderHistory } from '@pages/OrderCreation/OrderHistory';
import { ProductProcessingTaskList } from '@pages/OrderProcessing/ProductProcessingTaskList';
import { OrderProductSelection } from '@pages/OrderCreation/OrderProductSelection';
import { ProductProcessingTaskDetails } from '@pages/OrderProcessing/ProductProcessingTaskDetails';
import { ProductInProgressDetails } from '@pages/OrderProcessing/ProductInProgressDetails';
import { ProductProcessedDetails } from '@pages/OrderProcessing/ProductProcessedDetails';
import { OrderPickupList } from '@pages/OrderProcessing/OrderPickupList';
import { InvoiceList } from '@pages/Invoice/InvoiceList';
import { OrderedItemsList } from '@pages/Invoice/OrderedItemsList';
import { InvoiceDetails } from '@pages/Invoice/InvoiceDetails';
import { AllInvoices } from '@pages/Invoice/AllInvoices';
import { AllNotifications } from '@pages/Notification/AllNotifications';
import { io } from 'socket.io-client';
import { useEffect, useState } from 'react';
import { useToast } from '@contexts/Toast';
import {
  shouldShowNotification,
  logNotificationFiltering,
} from '@helpers/NotificationUtils';
import { NotificationManager as NotificationService } from '@services/NotificationManager';
import { Pickup } from '@pages/ProductsSettings/Pickup';
import { ProfileDetails } from '@pages/Profile/ProfileDetails';
import { ServiceProcessServiceDetails } from '@pages/OrderProcessing/ServiceProcessServiceDetails';
import { StoreSettingsAddress } from '@pages/MyStoreSettings/StoreAddress';
import { StoreSettingsLogoUpload } from '@pages/MyStoreSettings/StoreLogo';
import { StoreSettingsTaxDetails } from '@pages/MyStoreSettings/StoreTaxDetails';
import { newOrderRoutes } from 'Routes/NewOrderRoutes';
import { PlanView } from '@pages/Subscription/PlanView';
import { PlanDetails } from '@pages/Subscription/PlanDetails';
import { SubscriptionConfirmation } from '@pages/Subscription/Popup/SubscriptionConfirmation';
import { SubscriptionFailed } from '@pages/Subscription/Popup/SubscriptionFailed';
import { AppSettings } from '@pages/AppSettings/AppSettings';
import { AboutApplication } from '@pages/AppSettings/AboutApplication';
import { FQA } from '@pages/AppSettings/FAQ';
import { SubscriptionDetails } from '@pages/AppSettings/Subscriptions/SubscriptionDetails';
import { BillingDetails } from '@pages/AppSettings/Subscriptions/BillingDetails';
import { BillingInvoiceDetails } from '@pages/AppSettings/Subscriptions/BillingInvoiceDetails';
import { ReactivateSubscription } from '@pages/AppSettings/SubscriptionStatus/ReactivateSubscription';
import { ReactivateSubscriptionDetails } from '@pages/AppSettings/SubscriptionStatus/ReactivateSubscriptionDetails';
import { ReactivateSubscriptionSuccess } from '@pages/AppSettings/SubscriptionStatus/ReactivateSubscriptionSuccess';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useSubscriptionRestricted } from '@hooks/useSubscriptionRestricted';
import { PaymentMethodUpdateConfirmation } from '@pages/Subscription/Popup/PaymentMethodUpdateConfirmation';
import { subscribeToAxiosLoader } from '@helpers/ApiService';
import {
  errorMessage,
  SubscriptionToastExcludedRoutes,
} from '@helpers/Constants';

/**
 * A layout component that renders the authenticated content.
 */
const AuthenticatedLayout: React.FC = () => {
  const isRestricted = useSubscriptionRestricted();
  return <div>{isRestricted ? null : <Outlet />}</div>;
};

/**
 * Helper to normalise a path (remove trailing slash, except for root).
 */
const normalizePath = (path: string) => {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed || '/';
};

/**
 * Route guard for authenticated users.
 * - If admin (adminFlag === '1'), enforce onboarding order.
 * - For non‑admin, skip onboarding order; apply role‑based access.
 */
const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  children,
}) => {
  const authToken = localStorage.getItem('authToken');
  const adminFlag = localStorage.getItem('adminFlag');
  const currentPath = window.location.pathname;
  const { showToastMessage } = useToast();

  if (!authToken) {
    return <Navigate to={FrontendRoutes.LOGIN} />;
  }

  // --- Admin: enforce onboarding order ---
  if (adminFlag === '1') {
    // Subscription exclusion check (unchanged)
    if (SubscriptionToastExcludedRoutes.includes(currentPath)) {
      return <>{children}</>;
    }

    // Onboarding order enforcement
    const onboardingStepsStr = localStorage.getItem('onboardingSteps');
    if (onboardingStepsStr) {
      try {
        const steps = JSON.parse(onboardingStepsStr) as Array<{
          stepsId: number;
          route: string;
          isComplete: number;
        }>;
        const sortedSteps = [...steps].sort((a, b) => a.stepsId - b.stepsId);
        const firstIncomplete = sortedSteps.find((step) => step.isComplete === 0);
        const normalizedCurrent = normalizePath(currentPath);

        const currentStep = sortedSteps.find((step) => {
          const normalizedStep = normalizePath(step.route);
          return normalizedStep === normalizedCurrent;
        });

        if (currentStep && firstIncomplete) {
          if (currentStep.stepsId > firstIncomplete.stepsId) {
            showToastMessage(
              'Please complete this step first to continue further.',
              'error'
            );
            return <Navigate to={firstIncomplete.route} replace />;
          }
        }
      } catch (error) {
        console.error('Error parsing onboardingSteps:', error);
      }
    }

    // Admin has access to all routes (after onboarding check)
    return <>{children}</>;
  }

  // --- Non‑admin: no onboarding order check ---
  // Allow notifications and profile details without role checks
  if (
    currentPath === FrontendRoutes.NOTIFICATION ||
    currentPath === FrontendRoutes.PROFILE_DETAILS
  ) {
    return <>{children}</>;
  }

  // Role‑based access for other routes (original logic)
  const routes = JSON.parse(localStorage.getItem('routes') || '[]');
  const allowedRoutes = routes.map((r: { route: string }) => r.route);
  const isAllowed = allowedRoutes.some((routePattern: string) => {
    const matcher = match(routePattern, { decode: decodeURIComponent });
    return matcher(currentPath) !== false;
  });

  return isAllowed ? (
    <>{children}</>
  ) : (
    <Navigate to={routes.length > 0 ? routes[0].route : '/'} />
  );
};

/**
 * Subscription route guard – displays a toast when access is restricted.
 */
const SubscriptionRouteGuard: React.FC = () => {
  const { showConfirmationToast } = UseConfirmationToast();
  const adminFlag = Number(localStorage.getItem('adminFlag'));
  const isRestricted = useSubscriptionRestricted();
  const location = useLocation();
  const navigate = useNavigate();
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (isRestricted && !toastShownRef.current) {
      toastShownRef.current = true;
      showConfirmationToast(
        adminFlag === 1
          ? errorMessage.SUBSCRIPTION_INACTIVE_ADMIN_ERROR
          : errorMessage.SUBSCRIPTION_INACTIVE_NON_ADMIN_ERROR,
        'Need an Active Plan',
        'info',
        () => {
          if (adminFlag === 0) {
            navigate(FrontendRoutes.PROFILE_DETAILS);
          }
        },
        'Okay'
      );
      if (location.key !== 'default') {
        navigate(-1);
      }
    }
    if (!isRestricted) {
      toastShownRef.current = false;
    }
  }, [location.pathname]);

  return null;
};

/**
 * Main App component with routing and socket setup.
 */
const App: React.FC = () => {
  const { showToastMessage } = useToast();
  const [userDetails, setUserDetails] = useState<string | null>(null);
  const [isAxiosLoading, setIsAxiosLoading] = useState(false);
  const userDetailsRef = useRef<string | null>(null);

  useEffect(() => {
    return subscribeToAxiosLoader(setIsAxiosLoading);
  }, []);

  useEffect(() => {
    const initialUserDetails = localStorage.getItem('userDetails');
    setUserDetails(initialUserDetails);
    userDetailsRef.current = initialUserDetails;

    const checkUserDetails = () => {
      const currentUserDetails = localStorage.getItem('userDetails');
      if (currentUserDetails !== userDetailsRef.current) {
        userDetailsRef.current = currentUserDetails;
        setUserDetails(currentUserDetails);
      }
    };

    const intervalId = setInterval(checkUserDetails, 1000);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userDetails') {
        setUserDetails(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    userDetailsRef.current = userDetails;
  }, [userDetails]);

  // Socket connection
  useEffect(() => {
    if (!userDetails) {
      console.log('No user details found, skipping socket connection');
      return;
    }

    const userData = JSON.parse(userDetails);
    if (!userData.butcherId) {
      console.error('Butcher ID not found in login response');
      return;
    }

    console.log('Setting up socket connection for authenticated user');

    const socket = io(SocketConfig.URL, {
      ...SocketConfig.OPTIONS,
      query: {
        roles: userData.roles,
        userId: userData.id,
        butcherId: userData.butcherId,
      },
    });

    socket.on('connect', () => {
      console.log(`Connected to socket server. Socket ID: ${socket.id}`);
      console.log(
        `User ${userData.id} (${userData.roles}) connected for butcher ${userData.butcherId}`
      );
    });

    socket.on('room-joined', (data) => {
      console.log('Successfully joined rooms:', data.rooms);
    });

    socket.on('butcher-room-status', (data) => {
      console.log('Butcher room status:', data);
      if (data.created) {
        console.log(`New butcher room created: butcher_${userData.butcherId}`);
      } else {
        console.log(
          `Joined existing butcher room: butcher_${userData.butcherId}`
        );
      }
    });

    socket.on('new-notification', (data: AppNotification) => {
      console.log('New notification received:', data);
      const userRoles = userData.roles || [];
      const willShow = shouldShowNotification(data, userRoles);
      logNotificationFiltering(data, userRoles, willShow);

      if (willShow) {
        NotificationService.addNotification(data);
        showToastMessage(
          data.message || 'You have a new notification',
          'success'
        );
      } else {
        console.log(
          'Notification filtered out - user does not have required role(s)'
        );
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    socket.io.on('error', (error) => {
      console.error('Socket.IO error:', error);
    });

    return () => {
      console.log('Cleaning up socket connection');
      socket.disconnect();
    };
  }, [userDetails, showToastMessage]);

  return (
    <>
      {isAxiosLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#CBE4DD] border-t-[#004834]" />
        </div>
      )}
      <BrowserRouter future={{ v7_startTransition: true }}>
        <SubscriptionRouteGuard />
        <Refine>
          <Routes>
            {/* Public Routes */}
            {newOrderRoutes}
            <Route path={FrontendRoutes.REGISTRATION} element={<Registration />} />
            <Route path={FrontendRoutes.LOGIN} element={<Login />} />
            <Route path={FrontendRoutes.FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route path={FrontendRoutes.RESET_PASSWORD} element={<ResetPassword />} />
            <Route path={FrontendRoutes.INVITE_USER} element={<ResetPassword />} />

            {/* Authenticated Routes */}
            <Route element={<AuthenticatedLayout />}>
              <Route
                path={FrontendRoutes.DASHBOARD}
                element={
                  <AuthenticatedRoute>
                    <Dashboard />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.LOGO}
                element={
                  <AuthenticatedRoute>
                    <LogoUpload />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.ADDRESS}
                element={
                  <AuthenticatedRoute>
                    <AddressComponent />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.TAX_DETAILS}
                element={
                  <AuthenticatedRoute>
                    <TaxDetails />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.SPECIES}
                element={
                  <AuthenticatedRoute>
                    <Species />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.SERVICES}
                element={
                  <AuthenticatedRoute>
                    <Services />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.COLOR}
                element={
                  <AuthenticatedRoute>
                    <Color />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.PRODUCTS}
                element={
                  <AuthenticatedRoute>
                    <Products />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.ADDONS}
                element={
                  <AuthenticatedRoute>
                    <Addons />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.PACKAGING_TYPE}
                element={
                  <AuthenticatedRoute>
                    <PackagingType />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={FrontendRoutes.SPECIES_AND_SERVICE}
                element={
                  <AuthenticatedRoute>
                    <SpeciesAndService />
                  </AuthenticatedRoute>
                }
              />
            </Route>

            {/* These routes are not nested under AuthenticatedLayout but still guarded */}
            <Route
              path={FrontendRoutes.ROLE_MANAGEMENT}
              element={
                <AuthenticatedRoute>
                  <RoleManagement />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.USER_MANAGEMENT}
              element={
                <AuthenticatedRoute>
                  <UserManagement />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.STORAGE_COST}
              element={
                <AuthenticatedRoute>
                  <Pickup />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_SPECIES_SELECTION}
              element={
                <AuthenticatedRoute>
                  <SpeciesSelection />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.OPERATION_MANAGEMENT}
              element={
                <AuthenticatedRoute>
                  <OperationManagement />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CUSTOMER_LIST}
              element={
                <AuthenticatedRoute>
                  <CustomerList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_PRODUCT}
              element={
                <AuthenticatedRoute>
                  <OrderProductSelection />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CUSTOMER_DETAILS}
              element={
                <AuthenticatedRoute>
                  <CustomerDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_CUSTOMER}
              element={
                <AuthenticatedRoute>
                  <EditCustomer />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.UPDATE_CUSTOMER}
              element={
                <AuthenticatedRoute>
                  <EditCustomer />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION}
              element={
                <AuthenticatedRoute>
                  <SpeciesCustomisation />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_CHECKIN}
              element={
                <AuthenticatedRoute>
                  <OrderCheckin />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_SERVICE}
              element={
                <AuthenticatedRoute>
                  <OrderService />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_SUMMARY}
              element={
                <AuthenticatedRoute>
                  <OrderSummary />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SPECIES_SUMMARY}
              element={
                <AuthenticatedRoute>
                  <SpeciesSummary />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_PACKAGING}
              element={
                <AuthenticatedRoute>
                  <OrderPackaging />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_PRODUCT_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ProductDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.CREATE_ORDER_VIEW_SUMMARY}
              element={
                <AuthenticatedRoute>
                  <ViewOrderSummary />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.ALL_ORDER_LIST}
              element={
                <AuthenticatedRoute>
                  <AllOrderList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.ORDER_DETAILS}
              element={
                <AuthenticatedRoute>
                  <OrderDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SERVICE_PROCESSING_LIST}
              element={
                <AuthenticatedRoute>
                  <ServiceProcessingList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SERVICE_PROCESS_TASK_LIST}
              element={
                <AuthenticatedRoute>
                  <ServiceProcessTaskList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SERVICE_PROCESS_TASK_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ServiceProcessTaskDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SERVICE_PROCESS_SERVICE_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ServiceProcessServiceDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PRODUCT_PROCESSING_LIST}
              element={
                <AuthenticatedRoute>
                  <ProductProcessingList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST}
              element={
                <AuthenticatedRoute>
                  <ProductProcessingTaskList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PRODUCT_PROCESSING_TASK_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ProductProcessingTaskDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PRODUCT_INPROGRESS_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ProductInProgressDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PRODUCT_COMPLETED_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ProductProcessedDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.ORDER_HISTORY}
              element={
                <AuthenticatedRoute>
                  <OrderHistory />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.INVOICE_LIST}
              element={
                <AuthenticatedRoute>
                  <InvoiceList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.INVOICE_ORDERED_ITEMS}
              element={
                <AuthenticatedRoute>
                  <OrderedItemsList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.ALL_INVOICES}
              element={
                <AuthenticatedRoute>
                  <AllInvoices />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.INVOICE_DETAILS}
              element={
                <AuthenticatedRoute>
                  <InvoiceDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.ORDER_PICKUP_LIST}
              element={
                <AuthenticatedRoute>
                  <OrderPickupList />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.NOTIFICATION}
              element={
                <AuthenticatedRoute>
                  <AllNotifications />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PROFILE_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ProfileDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.STORE_LOGO}
              element={
                <AuthenticatedRoute>
                  <StoreSettingsLogoUpload />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.STORE_ADDRESS}
              element={
                <AuthenticatedRoute>
                  <StoreSettingsAddress />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.STORE_TAX}
              element={
                <AuthenticatedRoute>
                  <StoreSettingsTaxDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.VIEW_PLANS}
              element={
                <AuthenticatedRoute>
                  <PlanView />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.VIEW_PLAN_DETAILS}
              element={
                <AuthenticatedRoute>
                  <PlanDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_CONFIRMATION}
              element={
                <AuthenticatedRoute>
                  <SubscriptionConfirmation />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_FAILED}
              element={
                <AuthenticatedRoute>
                  <SubscriptionFailed />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.APP_SETTINGS}
              element={
                <AuthenticatedRoute>
                  <AppSettings />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.ABOUT_APPLICATION}
              element={
                <AuthenticatedRoute>
                  <AboutApplication />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.HELP_FAQ}
              element={
                <AuthenticatedRoute>
                  <FQA />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_DETAILS}
              element={
                <AuthenticatedRoute>
                  <SubscriptionDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_BILLING_DETAILS}
              element={
                <AuthenticatedRoute>
                  <BillingDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_BILLING_INVOICE_DETAILS}
              element={
                <AuthenticatedRoute>
                  <BillingInvoiceDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_REACTIVATE}
              element={
                <AuthenticatedRoute>
                  <ReactivateSubscription />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_REACTIVATE_PLANS_DETAILS}
              element={
                <AuthenticatedRoute>
                  <ReactivateSubscriptionDetails />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.SUBSCRIPTION_REACTIVATE_SUCCESS}
              element={
                <AuthenticatedRoute>
                  <ReactivateSubscriptionSuccess />
                </AuthenticatedRoute>
              }
            />
            <Route
              path={FrontendRoutes.PAYMENT_METHOD_UPDATE_SUCCESS}
              element={
                <AuthenticatedRoute>
                  <PaymentMethodUpdateConfirmation />
                </AuthenticatedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to={FrontendRoutes.LOGIN} />} />
          </Routes>
        </Refine>
      </BrowserRouter>
    </>
  );
};

export default App;